import { capture } from "@/lib/posthog";
import React from "react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// sessionStorage key for the one-reload guard. Cleared on every page load
// (sessionStorage scope = tab), so a *new* tab opened after the deploy never
// has it set, and a tab that already reloaded once for a stale-bundle error
// won't reload again — even if the new bundle is also broken — so we never
// loop the user.
const CHUNK_RELOAD_FLAG = "fastn:chunk-reload";

/**
 * Match the error shapes thrown when a dynamic `import()` can't fetch its
 * chunk because the file is gone (typical post-deploy: the old bundle was
 * pointing at a hash that the new deploy already deleted from /assets/).
 *
 * Covers Vite, Webpack, and native browser dynamic-import messages. Pure —
 * exported so unit tests can pin the exact set of strings we treat as
 * "reload to recover".
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: unknown; message?: unknown };
  if (typeof err.name === "string" && err.name === "ChunkLoadError") return true;
  const message =
    typeof err.message === "string"
      ? err.message
      : typeof error === "string"
        ? error
        : "";
  return (
    /Failed to fetch dynamically imported module/i.test(message)
    || /Loading chunk \d+ failed/i.test(message)
    || /Importing a module script failed/i.test(message)
    || /Unable to preload CSS/i.test(message)
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, info.componentStack);

    // Reported as TWO different events on purpose.
    //
    // ui_crashed is a real defect worth alerting on. chunk_load_failed is
    // almost always someone holding a stale bundle after a deploy, whose fix is
    // the reload below rather than a code change. Counting them together makes
    // every deploy look like a crash wave and trains the team to ignore the
    // alert — so the split has to happen here, at the only place that can tell
    // them apart.
    const route = typeof window === "undefined" ? "" : window.location.pathname;
    if (isChunkLoadError(error)) {
      capture("chunk_load_failed", { route });
    } else {
      capture("ui_crashed", {
        route,
        // First frame of the component stack: the component that actually
        // threw, not the whole tree.
        component: info.componentStack?.trim().split("\n")[0]?.trim() ?? null,
        error_name: error.name,
      });
    }

    // Stale bundle after a deploy: every code-split route's lazy import is
    // permanently broken until the shell is re-fetched. Try a single reload;
    // the guard flag stops us looping if the *new* bundle still throws.
    if (isChunkLoadError(error)) {
      try {
        if (typeof window === "undefined") return;
        if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === "1") return;
        window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
        window.location.reload();
      } catch {
        // sessionStorage can throw (privacy mode, quota) — fall through to
        // the normal error UI rather than crash inside the boundary itself.
      }
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 text-center">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              Something went wrong
            </h2>
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred. Please try again.
            </p>
          </div>
          <Button onClick={this.handleReset}>Try again</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
